import { Disclosure } from "@headlessui/react";
import NextLinkOrig from "next/link";
import { cn } from "~/lib/utils";
import { FeatureFlag } from "~/modules/feature_flag/components/FeatureFlag";
import { Badge } from "../Badge";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { NavItem } from "./types";
import { areNavItemsActive, isNavItemActive } from "./isNavItemActive";
import { useState } from "react";
import {
    faChevronDown,
    faChevronLeft,
} from "@fortawesome/pro-regular-svg-icons";
import { Icon } from "../Icon";

// // // //

export function MobileNavItem(props: {
    item: NavItem;
    nested?: boolean;
    activePathname: string;
}) {
    const { item, nested = false, activePathname } = props;
    const { items = [], defaultExpandedState = false } = item;

    const isActive = isNavItemActive({ item, pathname: activePathname });
    const hasActiveChild = areNavItemsActive({
        items,
        pathname: activePathname,
    });

    const [expanded, setExpanded] = useState(
        hasActiveChild || defaultExpandedState,
    );

    const linkClassName = cn("block border-l-4 py-2 pl-3 pr-4 text-base", {
        "flex justify-between": item.badge !== undefined,
        "border-blue-500 text-blue-700 bg-blue-50 cursor-default":
            isActive && !hasActiveChild,
        "text-link": hasActiveChild,
        "border-transparent hover:bg-slate-50 hover:border-border hover:text-slate-800 cursor-pointer":
            !isActive && !hasActiveChild,
    });

    const linkContent = (
        <span className="flex items-center w-full">
            {item.icon && (
                <Icon
                    className={cn("w-4 h-4", item.iconClassName, {
                        invisible: nested,
                        "mr-2": true,
                    })}
                    icon={item.icon}
                />
            )}
            <span className="text-base font-sans font-normal leading-normal whitespace-nowrap">
                {item.label}
            </span>
            {item.badge && (
                <Badge
                    label={item.badge.text}
                    variant={item.badge.variant}
                    rounded="md"
                    className="ml-2 whitespace-nowrap"
                    size="xs"
                />
            )}

            {items.length > 0 && (
                <button
                    className="ml-2 p-2"
                    onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        setExpanded(!expanded);
                    }}
                >
                    <FontAwesomeIcon
                        icon={expanded ? faChevronDown : faChevronLeft}
                        className={cn("h-[16px] w-[16px", {
                            "text-white cursor-default":
                                isActive && !hasActiveChild,
                            "text-link": hasActiveChild,
                            "text-slate-600 hover:text-link":
                                !isActive && !hasActiveChild,
                        })}
                    />
                </button>
            )}
        </span>
    );

    return (
        <FeatureFlag
            key={item.key}
            feature={item.featureFlag}
            invert={item.invertFeatureFlagBehavior}
        >
            {/* Open item in new tab */}
            {item.external && (
                <Disclosure.Button
                    as="a"
                    href={item.href}
                    target="_blank"
                    rel="noreferrer"
                    className={linkClassName}
                >
                    {linkContent}
                </Disclosure.Button>
            )}

            {!item.external && item.href !== undefined && (
                <NextLinkOrig href={item.href} legacyBehavior prefetch={false}>
                    <Disclosure.Button as="a" className={linkClassName}>
                        {linkContent}
                    </Disclosure.Button>
                </NextLinkOrig>
            )}

            {item.href === undefined && (
                <div className={linkClassName}>{linkContent}</div>
            )}

            {expanded && (
                <>
                    {items.map((n) => {
                        return (
                            <MobileNavItem
                                key={n.key}
                                item={n}
                                nested={true}
                                activePathname={activePathname}
                            />
                        );
                    })}
                </>
            )}
        </FeatureFlag>
    );
}
